import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";

import useEmblaCarousel from "embla-carousel-react";
import { useNavigate, useParams } from "react-router-dom";

import LeftMenu from "../components/LeftMenu";
import MobileMenu from "../components/MobileMenu";
import { SPORT_ICONS, LEAGUES } from "../data/leaguesConfig";
import { getTeamByName } from "../data/teamsDatabase";
import { sportsDbFetch } from "../utils/sportsDbCache";
import { getFavoriteTeams } from "../utils/sportsPreferences";
import webPlayerStorage from "../utils/webPlayerStorage";

// ─── SPORT NAME MAPPING ───
// Maps URL slug (categoryId) → TheSportsDB sport name
const SPORT_SLUG_MAP = {
  football: "Soccer",
  basketball: "Basketball",
  "american-football": "American Football",
  baseball: "Baseball",
  hockey: "Ice Hockey",
  motorsports: "Motorsport",
  fighting: "Fighting",
  rugby: "Rugby",
  volleyball: "Volleyball",
  afl: "Australian Football",
  cricket: "Cricket",
  "racket-sports": "Tennis",
  golf: "Golf",
  other: "Other",
  // Legacy slugs
  nfl: "American Football",
  ncaaf: "American Football",
  nba: "Basketball",
  nhl: "Ice Hockey",
  mlb: "Baseball",
  soccer: "Soccer",
  "ice-hockey": "Ice Hockey",
  motorsport: "Motorsport",
};

// CDN background images keyed by categoryId
const SPORT_CDN_BACKGROUNDS = {
  football: "https://d1hm15kdjuu43j.cloudfront.net/sports-bg/v2/football.jpg",
  basketball:
    "https://d1hm15kdjuu43j.cloudfront.net/sports-bg/v2/basketball.jpg",
  "american-football":
    "https://d1hm15kdjuu43j.cloudfront.net/sports-bg/v2/american-football.jpg",
  baseball: "https://d1hm15kdjuu43j.cloudfront.net/sports-bg/v2/baseball.jpg",
  hockey: "https://d1hm15kdjuu43j.cloudfront.net/sports-bg/v2/hockey.jpg",
  motorsports:
    "https://d1hm15kdjuu43j.cloudfront.net/sports-bg/v2/motorsports.jpg",
  fighting: "https://d1hm15kdjuu43j.cloudfront.net/sports-bg/v2/fighting.jpg",
  rugby: "https://d1hm15kdjuu43j.cloudfront.net/sports-bg/v2/rugby.jpg",
  volleyball:
    "https://d1hm15kdjuu43j.cloudfront.net/sports-bg/v2/volleyball.jpg",
  afl: "https://d1hm15kdjuu43j.cloudfront.net/sports-bg/v2/afl.jpg",
  cricket: "https://d1hm15kdjuu43j.cloudfront.net/sports-bg/v2/cricket.jpg",
  "racket-sports":
    "https://d1hm15kdjuu43j.cloudfront.net/sports-bg/v2/racket-sports.jpg",
  golf: "https://d1hm15kdjuu43j.cloudfront.net/sports-bg/v2/golf.jpg",
  other: "https://d1hm15kdjuu43j.cloudfront.net/sports-bg/v2/other.jpg",
};

// Sport icons keyed by categoryId
const SPORT_CATEGORY_ICONS = {
  football: "fas fa-futbol",
  basketball: "fas fa-basketball-ball",
  "american-football": "fas fa-football-ball",
  baseball: "fas fa-baseball-ball",
  hockey: "fas fa-hockey-puck",
  motorsports: "fas fa-flag-checkered",
  fighting: "fas fa-fist-raised",
  rugby: "fas fa-football-ball",
  volleyball: "fas fa-volleyball-ball",
  afl: "fas fa-football-ball",
  cricket: "fas fa-cricket",
  "racket-sports": "fas fa-table-tennis",
  golf: "fas fa-golf-ball",
  other: "fas fa-trophy",
};

// ─── LEAGUE GROUPING LOGIC ───
const groupLeagues = (leagues) => {
  const featured = [];
  const major = [];
  const cups = [];
  const international = [];
  const minor = [];

  leagues.forEach((league) => {
    const isCup = Number(league.idCup) === 1;
    const division = Number(league.intDivision) || 999;
    const country = (league.strCountry || "").toLowerCase();
    const name = (league.strLeague || "").toLowerCase();

    // International detection
    const isInternational =
      country === "world" ||
      country === "international" ||
      name.includes("world") ||
      name.includes("olympic") ||
      name.includes("international");

    if (isInternational) {
      international.push(league);
    } else if (isCup) {
      cups.push(league);
    } else if (division >= 100) {
      minor.push(league);
    } else {
      major.push(league);
    }
  });

  // Sort major by division
  major.sort(
    (a, b) => (Number(a.intDivision) || 99) - (Number(b.intDivision) || 99)
  );

  // Pick top 6 featured from major leagues (division 1 first)
  const div1 = major.filter((l) => Number(l.intDivision) === 1);
  const topLeagues =
    div1.length >= 6
      ? div1.slice(0, 6)
      : [...div1, ...major.filter((l) => Number(l.intDivision) !== 1)].slice(
          0,
          6
        );

  topLeagues.forEach((l) => featured.push(l));

  return { featured, major, cups, international, minor };
};

// ─── TAB CONFIG ───
const TABS = [
  { key: "all", label: "All", icon: "fa-th-large" },
  { key: "major", label: "Major Leagues", icon: "fa-star" },
  { key: "cups", label: "Cup Competitions", icon: "fa-trophy" },
  { key: "international", label: "International", icon: "fa-globe" },
  { key: "minor", label: "Minor / Development", icon: "fa-seedling" },
];

// Find the matching LEAGUES config entry by sportsDbId for proper routing
const getLeagueRoute = (league) => {
  const sportsDbId = Number(league.idLeague);
  const configMatch = LEAGUES.find((l) => l.sportsDbId === sportsDbId);

  if (configMatch) {
    return `/web-player/sports/league/${configMatch.id}`;
  }

  // Use league detail page directly — it handles TheSportsDB-only leagues
  return `/web-player/sports/league/${league.idLeague}`;
};

// ─── LEAGUE CARD COMPONENT ───
const LeagueCard = ({ league, size = "medium", navigate }) => {
  const isLarge = size === "large";

  return (
    <button
      className={`${
        isLarge
          ? "bg-white/[0.04] rounded-2xl p-6 border border-white/[0.08] hover:border-blue-500/40 transition-all group text-left relative overflow-hidden"
          : "shrink-0 w-[200px] bg-white/[0.04] rounded-xl p-5 border border-white/[0.08] hover:border-blue-500/40 transition-all text-center group"
      }`}
      onClick={() => {
        if (league.idLeague) {
          navigate(getLeagueRoute(league));
        }
      }}
    >
      {isLarge && (
        <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-blue-500 to-teal-400 opacity-0 group-hover:opacity-100 transition-opacity" />
      )}

      {isLarge ? (
        <>
          <div className="flex items-center gap-4 mb-4">
            {league.strBadge || league.strLogo ? (
              <img
                alt={league.strLeague}
                className="w-14 h-14 object-contain rounded-xl"
                src={league.strBadge || league.strLogo}
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            ) : (
              <div className="w-14 h-14 rounded-xl bg-blue-600/20 flex items-center justify-center">
                <span className="text-lg font-bold text-blue-400">
                  {(league.strLeague || "?").substring(0, 3)}
                </span>
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h3 className="text-white font-bold text-lg truncate group-hover:text-blue-300 transition-colors">
                {league.strLeague}
              </h3>
              <p className="text-white/40 text-xs">{league.strCountry}</p>
            </div>
          </div>
          {league.strCurrentSeason && (
            <div className="mb-3">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-green-500/12 text-green-400 border border-green-500/20">
                <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                {league.strCurrentSeason} Season
              </span>
            </div>
          )}
          <div className="flex gap-4 pt-3 border-t border-white/[0.06]">
            {league.intFormedYear && (
              <div>
                <p className="text-white font-bold text-sm">
                  {league.intFormedYear}
                </p>
                <p className="text-[10px] text-white/25 font-semibold uppercase tracking-wider">
                  Founded
                </p>
              </div>
            )}
            <div>
              <p className="text-white font-bold text-sm">
                {league.strCountry}
              </p>
              <p className="text-[10px] text-white/25 font-semibold uppercase tracking-wider">
                Country
              </p>
            </div>
          </div>
        </>
      ) : (
        <>
          {league.strBadge || league.strLogo ? (
            <img
              alt={league.strLeague}
              className="w-11 h-11 object-contain mx-auto mb-3 rounded-lg"
              src={league.strBadge || league.strLogo}
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          ) : (
            <div className="w-11 h-11 rounded-lg bg-blue-600/20 flex items-center justify-center mx-auto mb-3">
              <span className="text-sm font-bold text-blue-400">
                {(league.strLeague || "?").substring(0, 3)}
              </span>
            </div>
          )}
          <h4 className="text-white text-[13px] font-bold truncate group-hover:text-blue-300 transition-colors">
            {league.strLeague}
          </h4>
          <p className="text-white/30 text-[11px] mt-1">{league.strCountry}</p>
        </>
      )}
    </button>
  );
};

// ─── SECTION COMPONENT ───
const LeagueSection = ({
  title,
  leagues,
  dotColor,
  count,
  navigate,
  sportSlug,
}) => {
  const [emblaRef] = useEmblaCarousel({
    loop: false,
    align: "start",
    containScroll: false,
    dragFree: true,
  });

  if (!leagues || leagues.length === 0) return null;

  return (
    <div className="mb-10">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span
            className="w-1.5 h-1.5 rounded-full"
            style={{ background: dotColor || "#3b82f6" }}
          />
          <h2 className="text-lg font-bold text-white">{title}</h2>
        </div>
        <span className="text-[11px] font-semibold text-white/20 uppercase tracking-widest">
          {count || leagues.length}{" "}
          {leagues.length === 1 ? "league" : "leagues"}
        </span>
      </div>
      <div className="overflow-visible" ref={emblaRef}>
        <div className="flex gap-3">
          {leagues.slice(0, 10).map((league) => (
            <LeagueCard
              key={league.idLeague}
              league={league}
              navigate={navigate}
            />
          ))}
          {leagues.length > 10 && (
            <button
              className="shrink-0 w-[200px] rounded-xl border border-dashed border-white/[0.15] flex flex-col items-center justify-center gap-2 hover:border-blue-500/40 hover:bg-white/[0.04] transition-all group"
              onClick={() =>
                navigate(`/web-player/sports/hub/${sportSlug}/all`, {
                  state: { category: title, leagues },
                })
              }
            >
              <div className="w-10 h-10 rounded-full bg-blue-500/15 flex items-center justify-center group-hover:scale-110 transition-transform">
                <i className="fas fa-arrow-right text-blue-400" />
              </div>
              <span className="text-white/40 text-xs font-semibold group-hover:text-blue-300 transition-colors">
                View All ({leagues.length})
              </span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── MAIN COMPONENT ───
const WebPlayerSportHub = () => {
  const { sportName } = useParams();
  const navigate = useNavigate();
  const hasCheckedProfile = useRef(false);

  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef(null);

  const profile = webPlayerStorage.getProfile();
  const playlist = webPlayerStorage.getPlaylist();

  // Resolve sport name from slug
  const slugKey = sportName?.toLowerCase();
  const sport = SPORT_SLUG_MAP[slugKey] || sportName || "Baseball";
  const sportIcon =
    SPORT_CATEGORY_ICONS[slugKey] || SPORT_ICONS[sport] || "fas fa-trophy";
  const sportBg = SPORT_CDN_BACKGROUNDS[slugKey] || "/sports-bg-basketball.png";

  // Get favorite teams for this sport
  const favoriteTeamNames = getFavoriteTeams();
  const favoriteTeams = favoriteTeamNames
    .map((name) => getTeamByName(name))
    .filter((t) => t && t.sport === sport);

  const fetchLeagues = useCallback(async () => {
    setLoading(true);
    try {
      const data = await sportsDbFetch(
        `search_all_leagues.php?s=${encodeURIComponent(sport)}`
      );
      const allLeagues =
        data?.countries || data?.countrys || data?.leagues || [];

      setLeagues(allLeagues);
    } catch (err) {
      console.error("Failed to fetch leagues for sport:", err);
    } finally {
      setLoading(false);
    }
  }, [sport]);

  useEffect(() => {
    if (!hasCheckedProfile.current) {
      hasCheckedProfile.current = true;
      const p = webPlayerStorage.getProfile();

      if (!p || !p.id) {
        navigate("/web-player/profiles");

        return;
      }
    }
    fetchLeagues();
  }, [fetchLeagues, navigate]);

  // Filter by search then group
  const filteredLeagues = useMemo(() => {
    if (!searchQuery.trim()) return leagues;
    const q = searchQuery.toLowerCase();

    return leagues.filter(
      (l) =>
        (l.strLeague || "").toLowerCase().includes(q) ||
        (l.strCountry || "").toLowerCase().includes(q)
    );
  }, [leagues, searchQuery]);

  const groups = groupLeagues(filteredLeagues);

  // Filter by active tab
  const showFeatured = activeTab === "all" && !searchQuery;
  const showMajor = activeTab === "all" || activeTab === "major";
  const showCups = activeTab === "all" || activeTab === "cups";
  const showInternational =
    activeTab === "all" || activeTab === "international";
  const showMinor = activeTab === "all" || activeTab === "minor";

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <i className="fas fa-spinner fa-spin text-4xl text-blue-500 mb-4" />
          <p className="text-white/40 text-sm">Loading {sport}...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-white">
      <LeftMenu
        isSportsPage
        activeProfile={profile}
        playlist={playlist}
        selectedSection="sports"
      />
      <MobileMenu activeProfile={profile} playlist={playlist} />

      {/* ─── SPORT BACKGROUND — full-bleed fixed layer ─── */}
      <div className="fixed inset-0 z-0">
        <img
          alt=""
          className="w-full h-full object-cover"
          src={sportBg}
          style={{ opacity: 0.6 }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(to bottom, transparent 40%, #0d1117 75%)",
          }}
        />
      </div>

      {/* ─── HERO CONTENT ─── */}
      <div className="relative z-10 pt-[50px] pb-8 min-h-[30vh] flex flex-col justify-end">
        {/* Back button */}
        <button
          className="absolute top-6 left-6 md:left-[88px] z-20 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors backdrop-blur-sm"
          onClick={() => navigate(-1)}
        >
          <i className="fas fa-arrow-left text-white" />
        </button>

        {/* Sport icon watermark */}
        <div className="absolute right-16 top-1/2 -translate-y-1/2 opacity-[0.04]">
          <i className={`${sportIcon} text-[180px] text-white`} />
        </div>

        {/* Hero text */}
        <div className="ml-0 md:ml-[72px] px-6 md:px-12">
          <div className="text-white/40 text-xs font-medium tracking-wider mb-2">
            <button
              className="text-blue-400 hover:text-blue-300 transition-colors"
              onClick={() => navigate("/web-player/sports")}
            >
              Sports
            </button>
            <span className="mx-2">&rsaquo;</span>
            {sport}
          </div>
          <h1 className="text-5xl md:text-6xl font-extrabold tracking-tight mb-2">
            {sport}
          </h1>
          <p className="text-white/40 text-sm font-medium">
            <span className="text-blue-400 font-semibold">
              {leagues.length}
            </span>{" "}
            Leagues
            <span className="mx-2">&middot;</span>
            <span className="text-blue-400 font-semibold">
              {new Set(leagues.map((l) => l.strCountry)).size}
            </span>{" "}
            Countries
          </p>
        </div>
      </div>

      {/* ─── CONTENT ─── */}
      <div className="relative z-10 ml-0 md:ml-[72px]">
        {/* Tab bar + Search */}
        <div className="px-6 md:px-12 mb-6">
          <div className="flex items-center gap-3 overflow-x-auto scrollbar-hide pb-1">
            {TABS.map((tab) => {
              const tabCount = {
                all: leagues.length,
                major: groups.major.length,
                cups: groups.cups.length,
                international: groups.international.length,
                minor: groups.minor.length,
              }[tab.key];

              if (tab.key !== "all" && tabCount === 0) return null;

              return (
                <button
                  className={`shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                    activeTab === tab.key
                      ? "bg-blue-500/20 text-blue-300 border border-blue-500/30"
                      : "bg-white/5 text-white/40 border border-white/[0.06] hover:bg-white/10 hover:text-white/60"
                  }`}
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                >
                  <i className={`fas ${tab.icon} text-xs`} />
                  {tab.label}
                  {tab.key !== "all" && (
                    <span className="text-[10px] opacity-50">{tabCount}</span>
                  )}
                </button>
              );
            })}

            {/* Search */}
            <div className="relative shrink-0">
              <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-white/20 text-xs" />
              <input
                className="w-48 pl-8 pr-8 py-2.5 bg-white/[0.06] border border-white/[0.08] rounded-xl text-sm text-white placeholder-white/30 focus:outline-none focus:border-blue-500/40 focus:w-64 transition-all"
                placeholder="Search..."
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors"
                  onClick={() => {
                    setSearchQuery("");
                    searchInputRef.current?.focus();
                  }}
                >
                  <i className="fas fa-times text-xs" />
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="px-6 md:px-12 pb-12">
          {/* Your Teams (only if user has favorites in this sport) */}
          {favoriteTeams.length > 0 && activeTab === "all" && (
            <div className="mb-10">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                  <h2 className="text-lg font-bold text-white">Your Teams</h2>
                </div>
                <button
                  className="text-xs text-blue-400 hover:text-blue-300 font-semibold transition-colors flex items-center gap-1"
                  onClick={() => navigate("/web-player/sports/teams")}
                >
                  Manage <i className="fas fa-arrow-right text-[10px]" />
                </button>
              </div>
              <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide">
                {favoriteTeams.map((team) => (
                  <button
                    className="shrink-0 flex items-center gap-3 bg-white/[0.04] rounded-xl px-4 py-3 border border-white/[0.08] hover:border-blue-500/40 transition-all group"
                    key={team.name}
                    onClick={() =>
                      navigate(
                        `/web-player/sports/team/${encodeURIComponent(team.name)}`
                      )
                    }
                  >
                    {team.logo ? (
                      <img
                        alt={team.name}
                        className="w-10 h-10 object-contain rounded-lg"
                        src={team.logo}
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-lg bg-blue-600/20 flex items-center justify-center">
                        <span className="text-sm font-bold text-blue-400">
                          {team.name.charAt(0)}
                        </span>
                      </div>
                    )}
                    <div className="text-left">
                      <p className="text-white text-sm font-semibold group-hover:text-blue-300 transition-colors">
                        {team.name}
                      </p>
                      <p className="text-white/30 text-[11px]">{team.league}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Featured leagues (large cards, only on All tab) */}
          {showFeatured && groups.featured.length > 0 && (
            <div className="mb-10">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                  <h2 className="text-lg font-bold text-white">
                    Featured Leagues
                  </h2>
                </div>
                <span className="text-[11px] font-semibold text-white/20 uppercase tracking-widest">
                  Top {groups.featured.length}
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {groups.featured.map((league) => (
                  <LeagueCard
                    key={league.idLeague}
                    league={league}
                    navigate={navigate}
                    size="large"
                  />
                ))}
              </div>
            </div>
          )}

          {/* Major leagues */}
          {showMajor && (
            <LeagueSection
              dotColor="#3b82f6"
              leagues={groups.major}
              navigate={navigate}
              sportSlug={slugKey}
              title="Major Leagues"
            />
          )}

          {/* Cup competitions */}
          {showCups && (
            <LeagueSection
              dotColor="#d4a73a"
              leagues={groups.cups}
              navigate={navigate}
              sportSlug={slugKey}
              title="Cup Competitions"
            />
          )}

          {/* International */}
          {showInternational && (
            <LeagueSection
              dotColor="#14b8a6"
              leagues={groups.international}
              navigate={navigate}
              sportSlug={slugKey}
              title="International"
            />
          )}

          {/* Minor / Development */}
          {showMinor && (
            <LeagueSection
              dotColor="rgba(255,255,255,0.3)"
              leagues={groups.minor}
              navigate={navigate}
              sportSlug={slugKey}
              title="Minor / Development"
            />
          )}

          {/* Popular Teams link */}
          <div className="mt-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                <h2 className="text-lg font-bold text-white">Teams</h2>
              </div>
              <button
                className="text-xs text-blue-400 hover:text-blue-300 font-semibold transition-colors flex items-center gap-1"
                onClick={() => navigate("/web-player/sports/teams")}
              >
                View All Teams <i className="fas fa-arrow-right text-[10px]" />
              </button>
            </div>
            <button
              className="w-full bg-white/[0.04] rounded-xl p-5 border border-white/[0.08] hover:border-blue-500/40 transition-all text-left group flex items-center gap-4"
              onClick={() => navigate("/web-player/sports/teams")}
            >
              <div className="w-12 h-12 rounded-full bg-blue-600/20 flex items-center justify-center">
                <i className="fas fa-users text-blue-400 text-lg" />
              </div>
              <div className="flex-1">
                <p className="text-white font-semibold group-hover:text-blue-300 transition-colors">
                  Browse {sport} Teams
                </p>
                <p className="text-white/30 text-xs">
                  View all teams, follow your favorites, and explore rosters
                </p>
              </div>
              <i className="fas fa-chevron-right text-white/20 group-hover:text-white/40 transition-colors" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WebPlayerSportHub;
